import React from 'react';

import type { ProjectPageLoader } from '@modules/project/type';
import { getDetailsItems } from './util/get-details-items';
import { useFeature } from '@growthbook/growthbook-react';
import { SectionNotAvailable } from '../details-accordion/SectionNotAvilable';

interface DetailsTableProps {
    data: ProjectPageLoader['project'];
    openEnquiryModal: () => void;
}

/**
 * Desktop version of the project details table component.
 *
 * Displays key project information in a structured table format for desktop screens.
 * Shows project title, region, completion year, floor area, and site area.
 */
export const DetailsTableDesktop = ({
    data,
    openEnquiryModal,
}: DetailsTableProps) => {
    const hasProjectAddressFeature = !!useFeature('feature_project_address');
    const detailsItems = getDetailsItems(data, hasProjectAddressFeature, true);
    const displayedDetailsItems = detailsItems.filter(
        (detail) => !detail.link && detail.value
    );
    if (displayedDetailsItems.length === 0) {
        return (
            <SectionNotAvailable
                description={
                    <>
                        No project details available for this project.
                        <br />
                        Request more information from this professional.
                    </>
                }
                enquiryButtonText="Request Project Details"
                openEnquiryModal={openEnquiryModal}
            />
        );
    }

    return (
        <div className="hidden pb-17 pt-4 md:flex">
            <div className="flex w-99 flex-col gap-2"></div>
            <dl className="ml-75 w-240 gap-24 text-5 text-[#232323]">
                {displayedDetailsItems.map((detail, index) => (
                    <div
                        key={`${detail.itemName}-${detail.value}-${index}`}
                        className="flex items-start gap-24"
                    >
                        <dt className="w-108 font-medium leading-[1.3] tracking-[0.025rem] text-[#bcbcbc]">
                            {detail.itemName}
                        </dt>
                        <dd className="w-108 text-[1.375rem] leading-[1.32]">
                            {detail.value}
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );
};
